import { translate } from "@/config/translator";
import { Input } from "@fluentui/react-components";
import ReactDOM from "react-dom";
import { useUserDetailsPopup } from "./userDetailsPopup/useUserDetailsPopup";
import { Dispatch, SetStateAction } from "react";
import MandatePopupElement from "./userDetailsPopup/mandatePopupElement";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/pro-light-svg-icons";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";
import {
  faArrowRightFromBracket,
  faGear,
} from "@fortawesome/pro-regular-svg-icons";
import { clsx } from "clsx/lite";
import { SlidingLabelButton } from "./userDetailsPopup/slidingLabelButton";

type UserDetailsPopupProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export function UserDetailsPopup({ isOpen, setIsOpen }: UserDetailsPopupProps) {
  const {
    lang,
    user,
    mandateSearchQuery,
    setMandateSearchQuery,
    onLogout,
    selectedMandate,
    onSelectMandate,
    isLoadingEmployeeMandates,
    employeeMandates,
    navigate,
    mandateSearchInputRef,
    mandateSearchInputOnKeyDown,
  } = useUserDetailsPopup({ setIsOpen });

  return ReactDOM.createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={() => {
          setIsOpen(false);
          setMandateSearchQuery("");
        }}
      />
      <div className={`hidden ${isOpen ? "tablet:block" : ""}`}>
        <div
          className={clsx(
            "absolute",
            "bg-white",
            "shadow-xl",
            "right-10",
            "top-24",
            "z-40",
            "rounded-xl",
            "w-[min(38vw,50rem)]",
            "overflow-hidden",
          )}
        >
          <div
            className={clsx(
              "relative",
              "flex",
              "flex-row",
              "justify-between",
              "items-center",
              "bg-canvas-secondary",
              "p-3",
              "px-4",
              "border-b",
              "border-neutral",
            )}
          >
            <div className="flex flex-col leading-3 pb-1">
              <div className="text-lg font-semibold">{user?.name}</div>
              <div className="text-font-secondary">{selectedMandate?.name}</div>
            </div>
            <div className="flex flex-row gap-4">
              {/* SETTINGS BUTTON */}
              <SlidingLabelButton
                onClick={() => {
                  navigate("/settings");
                  setIsOpen(false);
                }}
                label={translate("settings", lang)}
                icon={faGear}
              />

              {/* SIGN OUT BUTTON */}
              <SlidingLabelButton
                onClick={onLogout}
                label={translate("signOut", lang)}
                icon={faArrowRightFromBracket}
              />
            </div>
          </div>

          {/* USER MANDATES */}
          {user && user.mandateRights.length !== 0 && (
            <div className="flex flex-col gap-4 p-3">
              {user.mandateRights.map((mandateRight) => (
                <MandatePopupElement
                  selectedMandateId={selectedMandate?.id}
                  mandate={mandateRight.mandate}
                  onSelectMandate={onSelectMandate}
                />
              ))}
            </div>
          )}

          {/* EMPLOYEE MANDATES */}
          {user &&
            (user.scopes.includes("admin") ||
              user.scopes.includes("employee")) &&
            (employeeMandates?.length === 0 &&
            mandateSearchQuery.length === 0 ? (
              <p className="text-font-secondary p-3">
                {translate("notAssignedToAnyMandateAsEmployee", lang)}
              </p>
            ) : (
              <>
                <div className="flex flex-row w-full p-3">
                  <Input
                    size="large"
                    className="w-full"
                    onKeyDown={mandateSearchInputOnKeyDown}
                    ref={mandateSearchInputRef}
                    contentBefore={<FontAwesomeIcon icon={faSearch} />}
                    onChange={(_, data) => setMandateSearchQuery(data.value)}
                    value={mandateSearchQuery}
                  />
                </div>
                {isLoadingEmployeeMandates ? (
                  <div className="aspect-square">
                    <FullScreenSpinner />
                  </div>
                ) : (
                  <div
                    className={clsx(
                      "flex",
                      "flex-col",
                      "p-3",
                      "max-h-100",
                      "overflow-y-auto",
                      "divide-neutral",
                      "divide-y",
                    )}
                  >
                    {employeeMandates?.length === 0 ? (
                      <p className="text-font-secondary p-3">
                        {translate(
                          "noMandatesFoundWithCurrentSearchQuery",
                          lang,
                        )}
                      </p>
                    ) : (
                      employeeMandates?.map((mandate) => (
                        <MandatePopupElement
                          selectedMandateId={selectedMandate?.id}
                          mandate={mandate}
                          onSelectMandate={onSelectMandate}
                        />
                      ))
                    )}
                  </div>
                )}
              </>
            ))}
        </div>
      </div>
    </>,
    document.getElementById("portal-root")!,
  );
}
